// Phase 8.7 — LiftedAtTheMoment Public Site Settings
// Maintenance toggle, announcement banner, hero copy, age-gate, footer.

import React, { useEffect, useState } from "react";
import { Save, Loader2, Globe, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

export default function AdminSiteSettings() {
  const [form, setForm] = useState({
    maintenance: false,
    announcement: "",
    hero_title: "",
    hero_subtitle: "",
    age_gate_text: "",
    footer_copy: "",
    instagram_handle: "",
    support_email: "",
    support_phone: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/storefront/site-settings");
        setForm((f) => ({ ...f, ...data, maintenance: !!data.maintenance }));
      } catch (e) { setErr(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const save = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      // Only send fields that have a value (or false for booleans)
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (typeof v === "boolean" || (v != null && v !== "")) payload[k] = v;
      }
      await api.patch("/admin/storefront/site-settings", payload);
      setSaved(true);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Site"
      title="Public Site Settings"
      description="Public-facing copy, maintenance flag, and contact details for the LiftedAtTheMoment storefront."
      status={form.maintenance ? "Maintenance ON" : "Live"}
      testId="admin-site-settings"
    >
      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm">{err}</div>}
      {loading && <div className="text-sm text-lifted-dim">Loading…</div>}

      {!loading && (
        <div className="space-y-4">
          <div className={`glass-panel p-4 flex items-start gap-3 ${form.maintenance ? "border border-amber-400/40" : ""}`}
               data-testid="site-maintenance">
            <AlertTriangle className={`h-5 w-5 ${form.maintenance ? "text-amber-300" : "text-lifted-dim"}`} />
            <div className="flex-1">
              <div className="text-sm text-lifted-text font-medium">Maintenance mode</div>
              <div className="text-xs text-lifted-dim">When ON, the public storefront shows a maintenance banner.</div>
            </div>
            <button onClick={() => set("maintenance", !form.maintenance)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] ${
                      form.maintenance ? "bg-amber-400/15 text-amber-200 border-amber-400/40" : "border-white/10 text-lifted-dim hover:text-white"
                    }`}
                    data-testid="site-maintenance-toggle">
              {form.maintenance ? "ON" : "OFF"}
            </button>
          </div>

          <div className="glass-panel p-5 space-y-3" data-testid="site-content">
            <Field label="Announcement banner" value={form.announcement || ""}
                   onChange={(v) => set("announcement", v)} testid="site-announcement" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Hero title" value={form.hero_title || ""}
                     onChange={(v) => set("hero_title", v)} testid="site-hero-title" />
              <Field label="Hero subtitle" value={form.hero_subtitle || ""}
                     onChange={(v) => set("hero_subtitle", v)} testid="site-hero-subtitle" />
            </div>
            <Field label="Age-gate text" value={form.age_gate_text || ""}
                   onChange={(v) => set("age_gate_text", v)} multiline testid="site-age-gate" />
            <Field label="Footer copy" value={form.footer_copy || ""}
                   onChange={(v) => set("footer_copy", v)} multiline testid="site-footer" />
          </div>

          <div className="glass-panel p-5 space-y-3" data-testid="site-contact">
            <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
              <Globe className="h-3 w-3 text-lifted-green" /> Contact & social
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Instagram handle" value={form.instagram_handle || ""}
                     onChange={(v) => set("instagram_handle", v)} testid="site-instagram" />
              <Field label="Support email" value={form.support_email || ""}
                     onChange={(v) => set("support_email", v)} testid="site-support-email" />
              <Field label="Support phone" value={form.support_phone || ""}
                     onChange={(v) => set("support_phone", v)} testid="site-support-phone" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving}
                    className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="site-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save settings
            </button>
            {saved && <span className="text-xs text-lifted-green" data-testid="site-saved">Saved.</span>}
          </div>
        </div>
      )}
    </AdminFeatureShell>
  );
}

function Label({ children }) {
  return <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">{children}</div>;
}

function Field({ label, value, onChange, multiline, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      {multiline ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          data-testid={testid} />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
          data-testid={testid} />
      )}
    </div>
  );
}
